import { useEffect, useId, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../components/shared/StyledComponents'
import { api, FeedFilters, SotaExportEntity, SotaExportFormat } from '../../../services/api'
import { FeedEntity } from './FeedFilterBar'

/**
 * "Exportar" on the SOTA page.
 *
 * Two scopes. *Todo el SOTA* is the whole dataset, one sheet per kind
 * (publicaciones, repositorios, papers, clusters, notas and, for admins,
 * asignaciones), with a summary sheet. *Lista actual* is the list on screen with
 * its filters, every page of it. Each comes as Excel or CSV. Excel is listed
 * first because it opens cleanly anywhere; the CSV is written the way a Spanish
 * Excel expects it (";" and decimal comma).
 *
 * The same export is reachable from the chatbot through
 * GET /linkedin/export/link, which returns a download URL instead of the file.
 */

export interface CurrentListExport {
    entity: FeedEntity
    filters: FeedFilters
    /** How the list is called in the menu ("Publicaciones", "Repositorios"…). */
    label: string
    /** Whether the reader narrowed the list: changes the hint, not the request. */
    filtered: boolean
}

interface Props {
    isAdmin: boolean
    /** The list on screen, when there is one (the Contenido tab). */
    currentList?: CurrentListExport
}

interface ExportChoice {
    key: string
    label: string
    /** Accessible name: the format alone is ambiguous when two groups offer it. */
    ariaLabel: string
    entity: SotaExportEntity
    format: SotaExportFormat
    filters?: FeedFilters
    fallbackName: string
}

/** The filename the server chose, from Content-Disposition, or a fallback. */
export const filenameFromDisposition = (header: string | undefined | null, fallback: string): string => {
    if (!header) return fallback
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim().replace(/"/g, ''))
        } catch {
            // malformed escape: fall through to the plain parameter
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(header)
    return plain ? plain[1].trim() : fallback
}

const blobText = (blob: Blob): Promise<string> =>
    typeof blob.text === 'function'
        ? blob.text()
        : new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(String(reader.result ?? ''))
              reader.onerror = () => reject(reader.error)
              reader.readAsText(blob)
          })

/** A failed blob request carries FastAPI's JSON error inside a Blob; read its detail. */
export const exportErrorMessage = async (err: any): Promise<string> => {
    const data = err?.response?.data
    try {
        if (data instanceof Blob) {
            const parsed = JSON.parse(await blobText(data))
            if (typeof parsed?.detail === 'string') return parsed.detail
        } else if (typeof data?.detail === 'string') {
            return data.detail
        }
    } catch {
        // not JSON: use the generic message
    }
    return 'No se pudo generar la exportación. Inténtalo de nuevo.'
}

const saveBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}

const today = () => new Date().toISOString().split('T')[0]

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  /* When the header wraps, stay at the right edge: the menu is anchored to the
     button's right side and would otherwise open off the left of the screen. */
  margin-left: auto;
`

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 30;
  width: 320px;
  max-width: calc(100vw - 32px);
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.sm};
`

const Group = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing.sm};
    padding-top: ${({ theme }) => theme.spacing.sm};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`

const GroupTitle = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const GroupHint = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const Item = styled.button`
  display: block;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: none;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${({ theme }) => theme.colors.secondary};
    outline: none;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.6;
  }
`

const ErrorText = styled.div`
  max-width: 320px;
  margin-top: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.danger};
  text-align: right;
`

export const SotaExportMenu: React.FC<Props> = ({ isAdmin, currentList }) => {
    const [open, setOpen] = useState(false)
    const [busy, setBusy] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const focusTriggerWhenIdle = useRef(false)
    const ids = useId()

    // The trigger is disabled while an export runs, and a disabled button cannot
    // take focus: hand it back once the export is over, not when the file lands.
    useEffect(() => {
        if (busy === null && focusTriggerWhenIdle.current) {
            focusTriggerWhenIdle.current = false
            triggerRef.current?.focus()
        }
    }, [busy])

    /** Close and give focus back to the trigger, so keyboard users are not dropped on <body>. */
    const closeMenu = () => {
        setOpen(false)
        triggerRef.current?.focus()
    }

    useEffect(() => {
        if (!open) return
        // Focus the first option, as a menu button is expected to.
        menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus()
        const onPointer = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeMenu()
        }
        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    /** Arrow keys, Home and End move between the options. */
    const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])
        if (items.length === 0) return
        const current = items.indexOf(document.activeElement as HTMLButtonElement)
        let next = -1
        if (e.key === 'ArrowDown') next = (current + 1) % items.length
        else if (e.key === 'ArrowUp') next = (current - 1 + items.length) % items.length
        else if (e.key === 'Home') next = 0
        else if (e.key === 'End') next = items.length - 1
        if (next >= 0) {
            e.preventDefault()
            items[next].focus()
        }
    }

    const runExport = async (choice: ExportChoice) => {
        setBusy(choice.key)
        setError(null)
        try {
            const res = await api.linkedin.exportSota({
                entity: choice.entity,
                format: choice.format,
                filters: choice.filters,
            })
            const filename = filenameFromDisposition(res.headers?.['content-disposition'], choice.fallbackName)
            saveBlob(res.data, filename)
            setOpen(false)
            focusTriggerWhenIdle.current = true
        } catch (err) {
            setError(await exportErrorMessage(err))
        } finally {
            setBusy(null)
        }
    }

    const everything: ExportChoice[] = [
        {
            key: 'all-xlsx',
            label: 'Excel (.xlsx)',
            ariaLabel: 'Todo el SOTA en Excel (.xlsx)',
            entity: 'all',
            format: 'xlsx',
            fallbackName: `sota_todo_${today()}.xlsx`,
        },
        {
            key: 'all-csv',
            label: 'CSV (.zip, un archivo por hoja)',
            ariaLabel: 'Todo el SOTA en CSV (.zip, un archivo por hoja)',
            entity: 'all',
            format: 'csv',
            fallbackName: `sota_todo_${today()}.zip`,
        },
    ]
    const list: ExportChoice[] = currentList
        ? [
              {
                  key: 'list-xlsx',
                  label: 'Excel (.xlsx)',
                  ariaLabel: `Lista actual (${currentList.label}) en Excel (.xlsx)`,
                  entity: currentList.entity,
                  format: 'xlsx',
                  filters: currentList.filters,
                  fallbackName: `sota_${currentList.entity}_${today()}.xlsx`,
              },
              {
                  key: 'list-csv',
                  label: 'CSV (.csv)',
                  ariaLabel: `Lista actual (${currentList.label}) en CSV (.csv)`,
                  entity: currentList.entity,
                  format: 'csv',
                  filters: currentList.filters,
                  fallbackName: `sota_${currentList.entity}_${today()}.csv`,
              },
          ]
        : []

    const renderItems = (choices: ExportChoice[]) =>
        choices.map((c) => (
            <Item
                key={c.key}
                role="menuitem"
                type="button"
                aria-label={c.ariaLabel}
                disabled={busy !== null}
                onClick={() => runExport(c)}
            >
                {busy === c.key ? 'Generando…' : c.label}
            </Item>
        ))

    return (
        <Wrapper ref={wrapperRef}>
            <Button
                ref={triggerRef}
                type="button"
                variant="secondary"
                size="sm"
                aria-haspopup="menu"
                aria-expanded={open}
                disabled={busy !== null}
                onClick={() => {
                    setError(null)
                    setOpen((v) => !v)
                }}
            >
                {busy ? 'Exportando…' : '⬇ Exportar'}
            </Button>

            {open && (
                <Menu ref={menuRef} role="menu" aria-label="Exportar SOTA" onKeyDown={onMenuKeyDown}>
                    <Group role="group" aria-labelledby={`${ids}-all`}>
                        <GroupTitle id={`${ids}-all`}>Todo el SOTA</GroupTitle>
                        <GroupHint>
                            Una hoja por tipo: publicaciones, repositorios, papers, clusters, notas
                            {isAdmin ? ' y asignaciones' : ''}, más un resumen.
                        </GroupHint>
                        {renderItems(everything)}
                    </Group>
                    {currentList && (
                        <Group role="group" aria-labelledby={`${ids}-list`}>
                            <GroupTitle id={`${ids}-list`}>Lista actual · {currentList.label}</GroupTitle>
                            <GroupHint>
                                {currentList.filtered
                                    ? 'Con los filtros aplicados, todas las páginas.'
                                    : 'Sin filtros: todas las entradas de la lista.'}
                            </GroupHint>
                            {renderItems(list)}
                        </Group>
                    )}
                </Menu>
            )}

            {error && <ErrorText role="alert">{error}</ErrorText>}
        </Wrapper>
    )
}

export default SotaExportMenu
